import { validateLoad } from "./performedRules";
import { isAllowedLoadIncrement } from "./previousPerformance";
import { loadPresentation, standardOlympicBarWeight } from "./executionUi";

// Phase 9C: renders the server's Suggested output (§13.6) in the unit and
// convention of today's field. The engine computes physical families in the
// reference's own unit; this module owns the conversion, the conservative
// flooring and the direction, which is derived from the FINAL rendered values.
// It never computes a recommendation: an intent that cannot be rendered as a
// real loadable value degrades to a direction, or to nothing.

export const KG_PER_LB = 0.45359237;
const PHYSICAL_FAMILIES = new Set(["DUMBBELL", "KETTLEBELL", "OLYMPIC_BARBELL"]);
const EPSILON = 1e-6;

// The loadable grid per family and unit, mirrored from the backend
// SUGGESTED_RULES.increments (§13.7). Used only to floor a converted value.
const LOADABLE_STEPS = Object.freeze({
  DUMBBELL: { LB: [{ step: 5 }], KG: [{ below: 10, step: 1 }, { step: 2 }] },
  KETTLEBELL: { LB: [{ step: 10 }], KG: [{ step: 4 }] },
  OLYMPIC_BARBELL: { LB: [{ step: 5 }], KG: [{ step: 2.5 }] },
});
const OLYMPIC_PER_SIDE_STEP = Object.freeze({ LB: 2.5, KG: 1.25 });

function clean(value) {
  return Math.round(value * 1e6) / 1e6;
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function convertLoadUnit(value, fromUnit, toUnit) {
  const amount = toNumber(value);
  if (amount == null || !["LB", "KG"].includes(fromUnit) || !["LB", "KG"].includes(toUnit)) {
    return null;
  }
  if (fromUnit === toUnit) return amount;
  return fromUnit === "LB" ? amount * KG_PER_LB : amount / KG_PER_LB;
}

function floorToStep(value, step) {
  return clean(Math.floor(clean(value / step) + EPSILON) * step);
}

// Never heavier than the value: the largest loadable value at or below it.
export function floorToLoadable(value, family, unit) {
  const bands = LOADABLE_STEPS[family]?.[unit];
  if (!bands || !(value >= 0)) return null;
  const band = bands.find((entry) => entry.below == null || value < entry.below);
  return floorToStep(value, band.step);
}

function isPhysical(family) {
  return PHYSICAL_FAMILIES.has(family);
}

// The load on its family's own scale, in its own unit: Olympic by total.
function scaleOf(load, family) {
  const value = toNumber(load?.value);
  if (value == null) return null;
  if (family !== "OLYMPIC_BARBELL" || load.convention === "TOTAL") return value;
  if (load.convention !== "PER_SIDE") return null;
  const bar = toNumber(load.barWeight?.value);
  return bar == null || load.barWeight.unit !== load.unit ? null : clean(2 * value + bar);
}

function checked(load, loadMode) {
  if (!load || !validateLoad(load, loadMode)) return null;
  return isAllowedLoadIncrement(load.value, loadMode, load.convention, load.unit) ? load : null;
}

// source: a load the engine emitted ({ value, unit, convention }); Olympic
// values arrive as a total. Returns a load that can be entered as-is in
// today's field, or null when no real loadable value exists.
function renderLoad(source, family, context) {
  const { unit, convention, loadMode, barWeight } = context;
  const value = toNumber(source?.value);
  if (value == null || !unit || !convention) return null;

  if (!isPhysical(family)) {
    // Machine-local values are never converted or normalized.
    if (source.unit !== unit || source.convention !== convention) return null;
    return checked({ value: String(value), unit, convention, barWeight: null }, loadMode);
  }

  const sameUnit = source.unit === unit;
  const exact = convertLoadUnit(value, source.unit, unit);
  if (exact == null) return null;

  if (family === "OLYMPIC_BARBELL") {
    if (source.convention !== "TOTAL") return null;
    if (convention === "TOTAL") {
      const total = sameUnit ? exact : floorToLoadable(exact, family, unit);
      return checked({ value: String(total), unit, convention, barWeight: null }, loadMode);
    }
    const bar = toNumber(barWeight?.value);
    if (convention !== "PER_SIDE" || bar == null || barWeight.unit !== unit) return null;
    if (exact < bar - EPSILON) return null;
    const perSide = floorToStep((exact - bar) / 2, OLYMPIC_PER_SIDE_STEP[unit]);
    return checked({
      value: String(perSide),
      unit,
      convention,
      barWeight: { value: String(barWeight.value), unit },
    }, loadMode);
  }

  if (source.convention !== convention) return null;
  const rendered = sameUnit ? exact : floorToLoadable(exact, family, unit);
  return checked({ value: String(rendered), unit, convention, barWeight: null }, loadMode);
}

// The reference load rendered exactly as a Suggested value is: the same unit,
// convention, grid and conservative flooring. Both sides of the comparison
// are then on the same final rendered scale, so a "same load" that floors to
// 40 lb is compared with a reference that also renders as 40 lb.
function renderedReferenceScale(reference, family, context) {
  const scale = scaleOf(reference, family);
  if (scale == null) return null;
  const source = {
    value: scale,
    unit: reference.unit,
    convention: family === "OLYMPIC_BARBELL" ? "TOTAL" : reference.convention,
  };
  const rendered = renderLoad(source, family, context);
  return rendered ? scaleOf(rendered, family) : null;
}

// One pure function for the arrow (§13.6): the final rendered value against
// the final rendered reference, on the same scale and in the same unit.
export function deriveDirection(renderedScale, referenceScaleValue) {
  if (renderedScale == null || referenceScaleValue == null) return null;
  if (renderedScale > referenceScaleValue + EPSILON) return "UP";
  if (renderedScale < referenceScaleValue - EPSILON) return "DOWN";
  return "SAME";
}

/**
 * Returns null (nothing to show) or:
 *   { kind, direction, load, floor, intent, basis, repTarget, holdReason, family }
 * kind:
 *   NUMERIC   - an exact Suggested value; `load` can fill the field
 *   FLOOR     - the previous load as a starting floor; `floor`, never a target
 *   DIRECTION - direction only; no value
 */
export function renderSuggested(suggested, context = {}) {
  if (!suggested?.intent) return null;
  const family = suggested.family;
  const base = {
    intent: suggested.intent,
    basis: suggested.basis ?? null,
    repTarget: suggested.repTarget ?? null,
    holdReason: suggested.holdReason ?? null,
    family,
    load: null,
    floor: null,
  };
  // A machine-local family compared in another unit is not comparable at all.
  if (!isPhysical(family) && context.unit && suggested.reference?.unit !== context.unit) {
    return null;
  }

  if (suggested.intent === "HEAVIER") return { ...base, kind: "DIRECTION", direction: "UP" };
  if (suggested.intent === "LIGHTER") return { ...base, kind: "DIRECTION", direction: "DOWN" };
  if (suggested.intent === "FLOOR_HEAVIER") {
    const floor = renderLoad(suggested.referenceFloor, family, context);
    return floor
      ? { ...base, kind: "FLOOR", direction: "UP", floor }
      : { ...base, kind: "DIRECTION", direction: "UP" };
  }

  const load = renderLoad(suggested.load, family, context);
  const direction = load
    ? deriveDirection(scaleOf(load, family), renderedReferenceScale(suggested.reference, family, context))
    : null;
  if (suggested.intent === "STEP_UP") {
    // A step-up whose rendered value is not strictly above the rendered
    // reference (flooring collapsed the increase) is not shown as a number
    // (§13.6): the intent stays, the value is withheld.
    return direction === "UP"
      ? { ...base, kind: "NUMERIC", direction, load }
      : { ...base, kind: "DIRECTION", direction: "UP" };
  }
  return direction ? { ...base, kind: "NUMERIC", direction, load } : null;
}

// Only an exact numeric Suggested may fill the load field.
export function suggestedFillLoad(rendered) {
  return rendered?.kind === "NUMERIC" ? rendered.load : null;
}

// Today's field for a set: its unit, convention and (Olympic per side) bar.
export function suggestedContextForSet({ profile, preferences = {}, set, defaultUnit = null } = {}) {
  const presentation = loadPresentation(profile, preferences, set?.load);
  const unit = set?.load?.unit || defaultUnit || preferences?.weightUnit || null;
  const perSide = profile?.loadMode === "OLYMPIC_BARBELL" && presentation.convention === "PER_SIDE";
  return {
    loadMode: profile?.loadMode || null,
    unit,
    convention: presentation.convention || null,
    barWeight: perSide
      ? (set?.load?.unit === unit && set.load.barWeight) || standardOlympicBarWeight(unit, preferences)
      : null,
  };
}
